import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "reactstrap";

import {
  ActivityButton,
  FeatureScheduleEditor,
  FeatureScheduleEditorRef,
} from "../../components/Common";
import { FeatureScheduleDto } from "../../interfaces/control-feature";
import {
  useGetTeacherScheduleQuery,
  useUpdateTeacherScheduleMutation,
} from "../../store/api/teacherAdminApi";
import { defaultFeatureSchedule } from "../../utils/featureScheduleUtils";
import {
  hasSeenTeacherScheduleHighlight,
  markTeacherScheduleHighlightSeen,
} from "../../utils/teacherScheduleHighlight";

type TProps = {
  showNewFeatureAlert?: boolean;
  onVisited?: () => void;
};

export const TeacherScheduleSettings: React.FC<TProps> = ({
  showNewFeatureAlert = false,
  onVisited,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<FeatureScheduleEditorRef>(null);
  const { data, isLoading } = useGetTeacherScheduleQuery();
  const [updateTeacherSchedule, { isLoading: isSaving }] =
    useUpdateTeacherScheduleMutation();

  const [schedule, setSchedule] = useState<FeatureScheduleDto>(
    defaultFeatureSchedule(),
  );
  const [error, setError] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(
    showNewFeatureAlert && !hasSeenTeacherScheduleHighlight(),
  );

  useEffect(() => {
    if (data?.schedule) {
      setSchedule({ ...defaultFeatureSchedule(), ...data.schedule });
    }
  }, [data]);

  useEffect(() => {
    onVisited?.();
  }, [onVisited]);

  const onSave = async () => {
    const validationError = editorRef.current?.validate() ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    const payload = editorRef.current?.buildPayload();
    if (!payload) return;

    await updateTeacherSchedule({ schedule: payload }).unwrap();
    markTeacherScheduleHighlightSeen();
    setShowAlert(false);
  };

  return (
    <div>
      {showAlert && (
        <Alert
          color="success"
          className="border-0 alert-dismissible fade show"
          toggle={() => {
            setShowAlert(false);
            markTeacherScheduleHighlightSeen();
          }}
        >
          <div className="d-flex align-items-start gap-2">
            <i className="ri-calendar-schedule-line fs-20 mt-1"></i>
            <div>
              <h6 className="alert-heading mb-1">{t("teacherScheduleNew")}</h6>
              <p className="mb-0">{t("teacherScheduleNewFeatureAlert")}</p>
            </div>
          </div>
        </Alert>
      )}

      <p className="text-muted">{t("teacherScheduleHint")}</p>

      {isLoading ? (
        <div className="text-muted">{t("loading")}...</div>
      ) : (
        <FeatureScheduleEditor
          ref={editorRef}
          key={data?.id ?? data?.teacherUserId ?? "schedule"}
          schedule={schedule}
          error={error}
        />
      )}

      <div className="mt-4">
        <ActivityButton
          title="save"
          inProgressTitle="saving"
          color="success"
          className="btn btn-success"
          type="button"
          onClick={onSave}
          inProgress={isSaving}
          disabled={isLoading || isSaving}
        />
      </div>
    </div>
  );
};
